import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { financialsApi, type CtcLine, type CtcLineInput } from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { ctcLineAmount } from '@/lib/wip';

/**
 * The cost-to-complete BUILDUP for one cost code.
 *
 * A typed CTC says what the PM thinks is left; it doesn't say why, so nobody
 * can review it. The buildup records the working — "4 crew × 12 days × 10 hrs
 * × $68" — and the code's CTC becomes the read-only sum.
 *
 * One line shape for every cost type, labelled per the code's reporting group,
 * so a Material line reads units × unit cost and never shows an empty Hours box.
 * Opened from the CTC cell, mirroring the GL drill-down on the Actual column.
 */

/** Which factors this cost type uses, and what to call them. Mirrors the API's
 *  lib/ctcBuildup FACTOR_LABELS — the two are a pair. */
const FACTORS: Record<string, { qty1: string | null; qty2: string | null; qty3: string | null; rate: string }> = {
  Labor:       { qty1: 'Crew',  qty2: 'Days', qty3: 'Hrs/day', rate: 'Rate $/hr' },
  Equipment:   { qty1: 'Units', qty2: 'Days', qty3: null,      rate: 'Rate $/day' },
  Material:    { qty1: 'Units', qty2: null,   qty3: null,      rate: 'Unit cost $' },
  Subcontract: { qty1: null,    qty2: null,   qty3: null,      rate: 'Amount $' },
  Other:       { qty1: 'Qty',   qty2: null,   qty3: null,      rate: 'Rate $' },
};
const factorsFor = (group: string | null | undefined) => FACTORS[group ?? ''] ?? FACTORS.Other;

const money = (v: number) => '$' + Math.round(v).toLocaleString('en-US');

type Draft = { description: string; qty1: string; qty2: string; qty3: string; rate: string };
const toDraft = (l: CtcLine): Draft => ({
  description: l.description ?? '',
  qty1: l.qty1 == null ? '' : String(l.qty1),
  qty2: l.qty2 == null ? '' : String(l.qty2),
  qty3: l.qty3 == null ? '' : String(l.qty3),
  rate: String(l.rate ?? ''),
});
const EMPTY: Draft = { description: '', qty1: '', qty2: '', qty3: '', rate: '' };

const css = `
  .ctc-ov { position:fixed; inset:0; background:rgba(54,54,54,.32); z-index:200; }
  .ctc-dw { position:fixed; top:0; right:0; bottom:0; width:640px; max-width:100vw; background:#fff; z-index:201;
            overflow-y:auto; box-shadow:-4px 0 24px rgba(0,0,0,.12); }
  .ctc-hd { padding:16px 20px; border-bottom:1px solid var(--ew-n-100); position:sticky; top:0; background:#fff; z-index:1;
            display:flex; justify-content:space-between; align-items:flex-start; gap:12px; }
  .ctc-ctx { display:flex; gap:18px; padding:12px 20px; border-bottom:1px solid var(--ew-n-100); background:var(--ew-n-50,#FAFAFA); }
  .ctc-ctx div { font:var(--ew-fw-regular) 11px/1.4 var(--ew-font); color:var(--ew-n-500); }
  .ctc-ctx b { display:block; font:var(--ew-fw-semibold) 14px/1.3 var(--ew-font); color:var(--ew-dark);
               font-variant-numeric:tabular-nums; margin-top:2px; }
  .ctc-tbl { width:100%; border-collapse:collapse; font-size:13px; }
  .ctc-tbl th { text-align:right; padding:8px 8px; font:var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing:.05em;
                text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; }
  .ctc-tbl th:first-child { text-align:left; }
  .ctc-tbl td { padding:5px 8px; border-bottom:1px solid #F2F2F1; text-align:right; font-variant-numeric:tabular-nums; }
  .ctc-tbl td:first-child { text-align:left; }
  .ctc-in { width:100%; border:1px solid var(--ew-n-200); border-radius:3px; padding:5px 7px; font:inherit;
            font-variant-numeric:tabular-nums; text-align:right; background:#fff; color:var(--ew-dark); }
  .ctc-in.desc { text-align:left; }
  .ctc-amt { font-weight:600; white-space:nowrap; }
  .ctc-x { border:none; background:none; color:var(--ew-n-400); cursor:pointer; font-size:15px; line-height:1; padding:2px 4px; }
  .ctc-x:hover { color:var(--ew-danger,#B03A2E); }
  .ctc-tot td { border-top:2px solid var(--ew-n-200); border-bottom:none; font-weight:700; padding-top:10px; }
  .ctc-ft { display:flex; justify-content:space-between; align-items:center; gap:10px; padding:14px 20px;
            border-top:1px solid var(--ew-n-100); position:sticky; bottom:0; background:#fff; }
  .ctc-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer;
             border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .ctc-btn.primary { background:var(--ew-green); border-color:var(--ew-green); color:#fff; }
  .ctc-btn:disabled { opacity:.5; cursor:not-allowed; }
  .ctc-note { padding:10px 20px 0; font:var(--ew-fw-regular) 12px/1.5 var(--ew-font); color:var(--ew-n-500); }
`;

export default function CtcBuildupDrawer({
  projectId, costCode, costTypeGroup, costCodeName, budget, actual, committed, lines, canEdit, onClose, onSaved,
}: {
  projectId: number;
  costCode: string;
  costTypeGroup: string;
  costCodeName: string | null;
  budget: number;
  actual: number;
  committed: number;
  lines: CtcLine[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });
  const f = factorsFor(costTypeGroup);
  const [draft, setDraft] = useState<Draft[]>(() => (lines.length ? lines.map(toDraft) : [{ ...EMPTY }]));
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(() => draft.reduce((s, l) => s + ctcLineAmount(l), 0), [draft]);
  const set = (i: number, k: keyof Draft, v: string) =>
    setDraft((d) => d.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  /**
   * ⚠️ Takes the rows to save as an ARGUMENT, never from `draft`.
   *
   * The first version had "Clear buildup" call `setDraft([])` then `save.mutate()`.
   * `setDraft` only schedules a re-render, so the mutation still closed over the
   * CURRENT, non-empty draft: it re-saved the very lines it was meant to delete,
   * reported success and closed the drawer, leaving the buildup untouched.
   * Raised in review on #280.
   */
  const save = useMutation({
    mutationFn: (rows: Draft[]) => {
      const num = (v: string) => (v.trim() === '' ? null : Number(v));
      // A row the PM added and never filled in isn't a line — drop it rather
      // than storing a $0 placeholder that clutters the buildup.
      const payload: CtcLineInput[] = rows
        .filter((l) => l.description.trim() !== '' || [l.qty1, l.qty2, l.qty3, l.rate].some((v) => v.trim() !== ''))
        .map((l) => ({
          description: l.description.trim() || null,
          // Only send the factors this cost type actually uses, so a group
          // change can't leave an orphaned Hours value quietly multiplying.
          qty1: f.qty1 ? num(l.qty1) : null,
          qty2: f.qty2 ? num(l.qty2) : null,
          qty3: f.qty3 ? num(l.qty3) : null,
          rate: num(l.rate) ?? 0,
        }));
      const bad = payload.find((l) => [l.qty1, l.qty2, l.qty3, l.rate].some((v) => v != null && !Number.isFinite(v)));
      if (bad) throw new Error('Every quantity and rate must be a number');
      return financialsApi.saveCtcLines(projectId, costCode, payload);
    },
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Failed to save'),
  });

  const cols = [f.qty1, f.qty2, f.qty3].filter(Boolean) as string[];

  return (
    <>
      <style>{css}</style>
      <div className="ctc-ov" onClick={onClose} />
      <div className="ctc-dw" ref={ref} role="dialog" aria-label={`Cost to complete buildup for ${costCode}`}>
        <div className="ctc-hd">
          <div>
            <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>
              Cost to Complete · Buildup
            </div>
            <div style={{ font: 'var(--ew-fw-semibold) 17px/1.3 var(--ew-font)', margin: '6px 0 2px' }}>{costCode}</div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
              {costCodeName ?? '—'} · {costTypeGroup}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="ctc-x" style={{ fontSize: 18 }}>✕</button>
        </div>

        {/* Context: you cannot forecast what's left without seeing what's gone. */}
        <div className="ctc-ctx">
          <div>Budget<b>{money(budget)}</b></div>
          <div>Actual to date<b>{money(actual)}</b></div>
          <div>Committed<b>{money(committed)}</b></div>
          <div>Remaining budget<b>{money(Math.max(budget - actual, 0))}</b></div>
        </div>

        <div className="ctc-note">
          {canEdit
            ? <>Each line is <b>{[...cols, f.rate].join(' × ')}</b>. The cost code's cost to complete is the total below,
               and while a buildup exists the CTC cell can't be typed into directly.</>
            : <>This code's cost to complete is built up from the lines below.</>}
        </div>

        <div style={{ padding: '10px 20px 0' }}>
          <table className="ctc-tbl">
            <thead>
              <tr>
                <th style={{ width: '38%' }}>Description</th>
                {cols.map((c) => <th key={c} style={{ width: 78 }}>{c}</th>)}
                <th style={{ width: 96 }}>{f.rate}</th>
                <th style={{ width: 104 }}>Amount</th>
                {canEdit ? <th style={{ width: 28 }} aria-label="Remove" /> : null}
              </tr>
            </thead>
            <tbody>
              {draft.map((l, i) => (
                <tr key={i}>
                  <td>
                    {canEdit
                      ? <input className="ctc-in desc" value={l.description} placeholder="What's left to do"
                               onChange={(e) => set(i, 'description', e.target.value)} />
                      : (l.description || '—')}
                  </td>
                  {(['qty1', 'qty2', 'qty3'] as const).map((k, ki) =>
                    ([f.qty1, f.qty2, f.qty3][ki] ? (
                      <td key={k}>
                        {canEdit
                          ? <input className="ctc-in" inputMode="decimal" value={l[k]} onChange={(e) => set(i, k, e.target.value)} />
                          : (l[k] || '—')}
                      </td>
                    ) : null))}
                  <td>
                    {canEdit
                      ? <input className="ctc-in" inputMode="decimal" value={l.rate} onChange={(e) => set(i, 'rate', e.target.value)} />
                      : (l.rate || '—')}
                  </td>
                  <td className="ctc-amt">{money(ctcLineAmount(l))}</td>
                  {canEdit ? (
                    <td>
                      <button className="ctc-x" aria-label={`Remove line ${i + 1}`}
                              onClick={() => setDraft((d) => (d.length === 1 ? [{ ...EMPTY }] : d.filter((_, j) => j !== i)))}>✕</button>
                    </td>
                  ) : null}
                </tr>
              ))}
              <tr className="ctc-tot">
                <td>Cost to complete</td>
                {cols.map((c) => <td key={c} />)}
                <td />
                <td className="ctc-amt">{money(total)}</td>
                {canEdit ? <td /> : null}
              </tr>
            </tbody>
          </table>

          {canEdit && (
            <button className="ctc-btn" style={{ marginTop: 10 }} onClick={() => setDraft((d) => [...d, { ...EMPTY }])}>
              + Add line
            </button>
          )}
          {error && (
            <div style={{ margin: '10px 0 0', padding: '7px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>
          )}
        </div>

        {canEdit && (
          <div className="ctc-ft">
            {/* Clearing is the honest way back to a typed CTC — the PM shouldn't
                have to delete lines one at a time to escape the buildup. */}
            <button className="ctc-btn" disabled={save.isPending}
                    onClick={() => { setError(null); save.mutate([]); }}>
              Clear buildup
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ctc-btn" onClick={onClose} disabled={save.isPending}>Cancel</button>
              <button className="ctc-btn primary" disabled={save.isPending}
                      onClick={() => { setError(null); save.mutate(draft); }}>
                {save.isPending ? 'Saving…' : 'Save buildup'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
